import {
  AddLinkOutlined,
  Close,
  OpenInNew,
  PrintOutlined,
  SaveOutlined,
  SettingsOutlined,
  TuneOutlined,
} from '@mui/icons-material'
import {
  Alert,
  AppBar,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import FormSelect from '../../../../base/components/Form/FormSelect'
import { useSecurity } from '../../../../base/contexts/SecurityContext'
import useAuth from '../../../../base/hooks/useAuth'
import { useUtils } from '../../../../base/hooks/useUtils.tsx'
import { useImpresoraActualizar } from '../api/apiImpresoraActualizar.ts'
import { invalidateImpresoraAsignacionListado } from '../api/apiImpresoraAsignacionListado.ts'
import { useImpresoraAsignacionUpsert } from '../api/apiImpresoraAsignacionUpsert.ts'
import { invalidateImpresoraListado, useImpresoraListado } from '../api/apiImpresoraListado.ts'
import { invalidateImpresoraPuntoVenta } from '../api/apiImpresoraPuntoVenta.ts'
import {
  ImpresionDestinatarioProps,
  MotivoImpresionProps,
  useImpresionDocumento,
} from '../hooks/useImpresionDocumento.ts'
import { useIsiPrint, useIsiPrintImpresora } from '../hooks/useIsiPrint.ts'
import { impresoraRoutesMap } from '../impresoraRoutesMap.ts'
import {
  IMPRESION_DOCUMENTO,
  IMPRESION_FORMATO,
  ImpresionDocumentoProps,
  ImpresionFormatoProps,
  ImpresoraProps,
} from '../interfaces/impresora.ts'
import type { EscposDocument, PrinterInfo, PrintResult } from '../sdk/isi-printer-client.ts'
import { ISI_PRINT_SOPORTADO } from '../services/dispositivo.ts'
import {
  getFormatosDocumento,
  getImpresionDocumentoLabel,
  IMPRESION_COPIAS_MAX,
  IMPRESION_FORMATO_LABEL,
  PERMISO_CONFIGURAR_IMPRESORAS,
} from '../services/impresionDocumento.ts'
import { imprimirEnNavegador, obtenerUrlPublicaPdf, PdfFuenteProps } from '../services/impresionPdf.ts'
import {
  ISI_PRINTER_STATUS_LABEL,
  mensajeIsiPrint,
  resolverImpresoraIsiPrint,
} from '../services/isiPrintClient.ts'
import {
  esImpresoraRed,
  impresoraFormToInput,
  impresoraToForm,
} from '../validator/impresoraInputValidator.ts'
import ImpresionDocumentoMovil from './ImpresionDocumentoMovil'
import IsiPrintEstadoPanel from './isiPrint/IsiPrintEstadoPanel'

export interface ImpresionDocumentoDialogProps {
  open: boolean
  onClose: () => void
  titulo?: string
  /** Sin documento solo se ofrece imprimir con el navegador (ej. reportes) */
  documento?: ImpresionDocumentoProps | null
  /** PDF a mostrar / imprimir */
  pdf: PdfFuenteProps | null
  /** Formato del PDF recibido */
  formato?: ImpresionFormatoProps
  /** Permite regenerar el PDF si al guardar se elige otro formato */
  obtenerPdf?: (formato: ImpresionFormatoProps) => Promise<PdfFuenteProps> | PdfFuenteProps
  /**
   * Ticket ESC/POS del documento: con impresora de rollo se imprime el ticket (igual que la impresión
   * silenciosa) y el PDF queda solo para la vista previa y el navegador.
   */
  obtenerEscpos?: () => Promise<EscposDocument> | EscposDocument
  filename?: string
  /** Motivo por el que no se imprimió directo; sin motivo el diálogo es un visor */
  motivo?: MotivoImpresionProps
  mensaje?: string | null
  /** Impresora sugerida por isi-print cuando no encontró la asignada (PRINTER_NOT_FOUND) */
  sugerencia?: string | null
  /** Se llama al imprimir desde el diálogo (navegador, impresora asignada o recién guardada) */
  onImpreso?: () => void
  /** Destinatario del documento, para las acciones extra (ej. enviar por WhatsApp) */
  destinatario?: ImpresionDestinatarioProps
  /**
   * Muestra "Enviar por WhatsApp" en el diálogo de impresión (y en el menú móvil). Por defecto false.
   * Requiere que el proyecto conecte la acción (`accionesExtra` del provider) y licencia `lw` vigente.
   */
  whatsapp?: boolean
}

interface OpcionFormato {
  value: ImpresionFormatoProps
  label: string
}

const SIN_IMPRESORAS: ImpresoraProps[] = []

/**
 * Visor de PDF con opciones de impresión. Se abre desde `useImpresionDocumento` cuando el
 * documento no se pudo imprimir directo, o como visor (PdfViewerDialog).
 * Permite imprimir con la impresora asignada (isi-print), con el diálogo del sistema, guardar la
 * impresora elegida para el punto de venta de la sesión o, si isi-print no la encontró, vincular la
 * impresora registrada con una detectada (guarda su ID de hardware).
 */
const ImpresionDocumentoDialogEscritorio: FunctionComponent<ImpresionDocumentoDialogProps> = ({
  open,
  onClose,
  titulo,
  documento,
  pdf: pdfInicial,
  formato: formatoInicial = IMPRESION_FORMATO.ROLLO,
  obtenerPdf,
  obtenerEscpos,
  filename,
  motivo,
  mensaje,
  sugerencia,
  onImpreso,
  destinatario,
  whatsapp = false,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const { user } = useAuth()
  const { toast, showError } = useUtils()
  const { hasStaticPermission } = useSecurity()
  const impresion = useImpresionDocumento()
  const isi = useIsiPrint()

  const documentoLabel = documento ? getImpresionDocumentoLabel(documento) : ''
  const asignacion = impresion.obtenerAsignacion(documento)
  const nombreAsignada = asignacion?.impresora?.nombre || asignacion?.nombreImpresora || ''

  // Si la impresora asignada no estaba lista, se vigila hasta que vuelva (TanStack, se detiene sola)
  const vigilarAsignada =
    open &&
    Boolean(asignacion) &&
    (motivo === 'impresora-no-disponible' ||
      motivo === 'impresora-no-encontrada' ||
      motivo === 'error-impresion')
  const asignada = useIsiPrintImpresora(asignacion?.nombreImpresora, {
    vigilar: vigilarAsignada,
    onLista: () => toast.success(`${nombreAsignada} está lista; puede reintentar`),
  })
  const licencia = impresion.licencia === true
  const puedeConfigurar = Boolean(documento) && licencia && hasStaticPermission(PERMISO_CONFIGURAR_IMPRESORAS)

  // PDF actual (puede regenerarse en otro formato al guardar)
  const [pdf, setPdf] = useState<PdfFuenteProps | null>(pdfInicial)
  const [formatoPdf, setFormatoPdf] = useState<ImpresionFormatoProps>(formatoInicial)
  useEffect(() => {
    setPdf(pdfInicial)
    setFormatoPdf(formatoInicial)
  }, [pdfInicial, formatoInicial])

  // La URL del Blob se crea y se libera en el mismo efecto. Con useMemo + cleanup, StrictMode
  // ejecuta el cleanup al montar (revoca la URL) y el memo conserva la URL ya revocada.
  const [pdfUrl, setPdfUrl] = useState<string | null>(() => (typeof pdf === 'string' ? pdf : null))
  useEffect(() => {
    if (!(pdf instanceof Blob)) {
      setPdfUrl(pdf)
      return
    }
    const url = URL.createObjectURL(pdf)
    setPdfUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [pdf])

  /** PDF que se ve si es una URL pública; si es un Blob del navegador, la URL del formato carta */
  const obtenerUrlPublica = () => obtenerUrlPublicaPdf(pdf, obtenerPdf)

  const [iframeLoading, setIframeLoading] = useState(true)
  useEffect(() => {
    if (open) setIframeLoading(true)
  }, [pdfUrl, open])

  // --- Formulario "guardar para este punto de venta" ---
  const mostrarFormInicial =
    puedeConfigurar && (motivo === 'sin-asignacion' || motivo === 'impresora-no-encontrada')
  const [mostrarForm, setMostrarForm] = useState(mostrarFormInicial)
  useEffect(() => setMostrarForm(mostrarFormInicial), [mostrarFormInicial])

  const { data: impresoras = SIN_IMPRESORAS, isLoading: cargandoImpresoras } = useImpresoraListado(
    { codigoSucursal: user?.sucursal?.codigo ?? 0 },
    { enabled: open && puedeConfigurar && mostrarForm },
  )
  const [impresoraId, setImpresoraId] = useState<string>('')
  const [formato, setFormato] = useState<ImpresionFormatoProps>(formatoInicial)
  const [copias, setCopias] = useState<number>(1)
  const [nombreImpresora, setNombreImpresora] = useState('')
  useEffect(() => {
    if (!open) return
    setImpresoraId(asignacion?.impresora?._id ?? '')
    setFormato(asignacion?.formato ?? formatoInicial)
    setCopias(asignacion?.copias ?? 1)
    setNombreImpresora('')
    // Solo al abrir: no pisar lo que el usuario está eligiendo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const opcionesFormato = useMemo<OpcionFormato[]>(
    () =>
      documento
        ? getFormatosDocumento(documento).map((f) => ({ value: f, label: IMPRESION_FORMATO_LABEL[f] }))
        : [],
    [documento],
  )
  const impresoraSeleccionada = impresoras.find((i) => i._id === impresoraId) ?? null
  const seleccionEsRed = esImpresoraRed(impresoraSeleccionada?.atributo1)

  const { mutateAsync: guardarAsignacion } = useImpresoraAsignacionUpsert()
  const { mutateAsync: actualizarImpresora } = useImpresoraActualizar()
  const [accion, setAccion] = useState<'imprimir' | 'navegador' | 'guardar' | 'vincular' | null>(null)

  // --- Vincular impresora no encontrada con una detectada por isi-print ---
  const puedeVincular =
    puedeConfigurar && motivo === 'impresora-no-encontrada' && isi.listo && Boolean(asignacion?.impresora)
  const [vinculoId, setVinculoId] = useState('')
  useEffect(() => {
    if (!puedeVincular) return
    // Preselecciona la sugerencia del agente (por nombre o ID), si la hay
    const sugerida = resolverImpresoraIsiPrint(isi.impresoras, sugerencia)?.impresora
    setVinculoId((actual) => actual || sugerida?.id || '')
  }, [puedeVincular, sugerencia, isi.impresoras])
  const vinculo: PrinterInfo | null = isi.impresoras.find((p) => p.id === vinculoId) ?? null

  console.log('vinculos', vinculo)

  /**
   * Guarda el ID elegido y reimprime: en la impresora registrada (`atributo1`) o, si la asignación
   * usaba otra impresora para este equipo, en la asignación del punto de venta.
   */
  const vincularEImprimir = async () => {
    const imp = asignacion?.impresora
    if (!documento || !asignacion || !imp?._id || !vinculo) return
    setAccion('vincular')
    try {
      const usaAlterna = asignacion.nombreImpresora !== imp.atributo1
      if (usaAlterna) {
        await guardarAsignacion({
          codigoSucursal: user?.sucursal?.codigo ?? 0,
          codigoPuntoVenta: user?.puntoVenta?.codigo ?? null,
          input: {
            documento,
            impresoraId: imp._id,
            formato: asignacion.formato,
            copias: asignacion.copias,
            nombreImpresora: vinculo.id,
          },
        })
      } else {
        await actualizarImpresora({
          id: imp._id,
          input: impresoraFormToInput({ ...impresoraToForm(imp), atributo1: vinculo.id }),
        })
        await invalidateImpresoraListado()
      }
      await Promise.all([invalidateImpresoraPuntoVenta(), invalidateImpresoraAsignacionListado()])
      toast.success(`${imp.nombre} vinculada a ${vinculo.name}`)

      // Se imprime en el formato mostrado (respeta el elegido por el usuario), igual que el silencioso
      const { resultado: r, doc } = await enviarComoSilencioso(vinculo.id, asignacion.copias, formatoPdf)
      seguir(r, vinculo.name, doc)
      toast.success(`Enviado a ${vinculo.name}`)
      onClose()
    } catch (e) {
      toast.error(mensajeIsiPrint(e))
    } finally {
      setAccion(null)
    }
  }

  /**
   * Sigue el trabajo enviado desde el diálogo: si luego se detiene o falla, se avisa y se puede
   * volver a abrir este documento. Los envíos del diálogo son reimpresiones intencionales (force).
   */
  const seguir = (r: PrintResult, impresoraNombre: string, doc: PdfFuenteProps) =>
    impresion.seguirTrabajo(r, {
      titulo: titulo ?? (documentoLabel || 'Documento'),
      impresora: impresoraNombre,
      params: documento
        ? {
            documento,
            titulo,
            obtenerPdf: obtenerPdf ?? (() => doc),
            obtenerEscpos,
            filename,
            formato: formatoPdf,
          }
        : undefined,
      pdf: doc,
      formato: formatoPdf,
    })

  /** PDF en el formato pedido (regenera si cambió y es posible) */
  const pdfEnFormato = async (f: ImpresionFormatoProps): Promise<PdfFuenteProps | null> => {
    if (f === formatoPdf || !obtenerPdf) return pdf
    const nuevo = await obtenerPdf(f)
    setPdf(nuevo)
    setFormatoPdf(f)
    return nuevo
  }

  /**
   * Envía igual que la impresión silenciosa (`enviarDocumento`): ticket ESC/POS si la impresora es de
   * rollo y hay ticket; si no, el PDF del formato indicado. Es una reimpresión intencional (force).
   */
  const enviarComoSilencioso = async (printer: string, copiasEnvio: number, f: ImpresionFormatoProps) => {
    if (!documento) throw new Error('Documento no definido')
    const params = {
      documento,
      titulo,
      obtenerPdf: async (fmt: ImpresionFormatoProps) => (await pdfEnFormato(fmt)) ?? pdf!,
      obtenerEscpos,
      filename,
    }
    const { resultado, pdf: enviado } = await impresion.enviarDocumento(
      params,
      { printer, copias: copiasEnvio },
      f,
      { force: true },
    )
    // Para seguir el trabajo y reabrir el diálogo hace falta el PDF aunque se haya impreso el ticket
    return { resultado, doc: enviado ?? pdf! }
  }

  const imprimirAsignada = async () => {
    if (!pdf || !asignacion) return
    setAccion('imprimir')
    try {
      // Se imprime en el formato mostrado (respeta el elegido por el usuario), igual que el silencioso
      const { resultado: r, doc } = await enviarComoSilencioso(
        asignacion.nombreImpresora,
        asignacion.copias,
        formatoPdf,
      )
      seguir(r, asignacion.impresora?.nombre || asignacion.nombreImpresora, doc)
      toast.success(`Enviado a ${asignacion.impresora?.nombre || asignacion.nombreImpresora}`)
      onImpreso?.()
      onClose()
    } catch (e) {
      toast.error(mensajeIsiPrint(e))
      void isi.refrescar()
    } finally {
      setAccion(null)
    }
  }

  const imprimirNavegador = async () => {
    if (!pdf) return
    setAccion('navegador')
    try {
      await imprimirEnNavegador(pdf, formatoPdf)
      onImpreso?.()
    } finally {
      setAccion(null)
    }
  }

  const guardarEImprimir = async () => {
    if (!documento || !impresoraSeleccionada) return
    const copiasValidas = Math.min(IMPRESION_COPIAS_MAX, Math.max(1, Math.trunc(Number(copias) || 1)))
    const alterno = seleccionEsRed ? '' : nombreImpresora.trim()
    setAccion('guardar')
    try {
      const guardada = await guardarAsignacion({
        codigoSucursal: user?.sucursal?.codigo ?? 0,
        codigoPuntoVenta: user?.puntoVenta?.codigo ?? null,
        input: {
          documento,
          impresoraId: impresoraSeleccionada._id,
          formato,
          copias: copiasValidas,
          nombreImpresora: alterno,
        },
      })
      await Promise.all([invalidateImpresoraPuntoVenta(), invalidateImpresoraAsignacionListado()])
      toast.success(`${impresoraSeleccionada.nombre} guardada para ${documentoLabel} en este punto de venta`)

      const printer = guardada?.nombreImpresora || alterno || impresoraSeleccionada.atributo1 || ''
      if (!printer) {
        toast.warning('La impresora no tiene nombre de sistema o IP; edítela en Impresoras para imprimir.')
        return
      }
      try {
        // La factura se imprime en el formato del usuario (el mostrado); el resto, en el elegido
        const f = documento === IMPRESION_DOCUMENTO.FACTURA ? formatoPdf : formato
        const { resultado: r, doc } = await enviarComoSilencioso(printer, copiasValidas, f)
        seguir(r, impresoraSeleccionada.nombre, doc)
        toast.success(`Enviado a ${impresoraSeleccionada.nombre}`)
        onImpreso?.()
        onClose()
      } catch (e) {
        toast.error(mensajeIsiPrint(e))
      }
    } catch (e) {
      showError(e)
    } finally {
      setAccion(null)
    }
  }

  const alerta = (() => {
    const impresoraTxt = asignacion?.impresora?.nombre || asignacion?.nombreImpresora
    switch (motivo) {
      case 'sin-licencia':
        return {
          severity: 'warning' as const,
          texto: `No hay licencia de impresión vigente; imprima con el navegador.${mensaje ? ` ${mensaje}` : ''}`,
        }
      case 'sin-configuracion':
        return {
          severity: 'warning' as const,
          texto: 'No se pudo obtener la configuración de impresoras del punto de venta.',
        }
      case 'sin-asignacion':
        return {
          severity: 'warning' as const,
          texto: `Este punto de venta no tiene impresora configurada para ${documentoLabel || 'este documento'}.`,
        }
      case 'impresora-no-encontrada':
        return {
          severity: 'warning' as const,
          texto: `isi-print no encontró la impresora “${impresoraTxt}” en este equipo.${
            sugerencia ? ` ¿Es “${sugerencia}”?` : ''
          }`,
        }
      case 'impresora-no-disponible':
        return {
          severity: 'warning' as const,
          texto: `La impresora “${impresoraTxt}” no está lista${mensaje ? `: ${mensaje.toLowerCase()}` : ''}. Revísela y reintente.`,
        }
      case 'error-impresion':
        return {
          severity: 'error' as const,
          texto: `No se pudo imprimir en “${impresoraTxt}”.${mensaje ? ` ${mensaje}` : ''}`,
        }
      default:
        return null
    }
  })()

  const ocupado = accion !== null

  // Con la impresora de nuevo lista, el aviso pasa a invitar a reintentar
  const alertaVisible =
    vigilarAsignada && asignada.online && motivo !== 'impresora-no-encontrada'
      ? {
          severity: 'success' as const,
          texto: `${nombreAsignada} ya está lista. Puede reintentar la impresión.`,
        }
      : alerta

  return (
    <Dialog
      open={open}
      onClose={ocupado ? undefined : onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="xl"
      slotProps={{
        paper: {
          sx: {
            height: isMobile ? '100%' : '95vh',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.default',
          },
        },
      }}
    >
      <AppBar position="relative" color="default" elevation={1} sx={{ flexShrink: 0 }}>
        <Toolbar variant="dense" sx={{ justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700 }}>
            {titulo ?? (documentoLabel || 'Vista previa')}
          </Typography>
          <IconButton edge="end" onClick={onClose} size="small" disabled={ocupado} aria-label="Cerrar">
            <Close />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Stack direction={isMobile ? 'column' : 'row'} sx={{ flex: 1, overflow: 'hidden' }}>
        {/* Panel de impresión */}
        <Box
          sx={{
            width: isMobile ? '100%' : 320,
            maxHeight: isMobile ? '45%' : 'none',
            flexShrink: 0,
            borderRight: isMobile ? 'none' : '1px solid',
            borderBottom: isMobile ? '1px solid' : 'none',
            borderColor: 'divider',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            overflowY: 'auto',
            bgcolor: 'background.paper',
          }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <PrintOutlined fontSize="small" color="primary" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Imprimir {documentoLabel}
            </Typography>
          </Stack>

          {alertaVisible && (
            <Alert severity={alertaVisible.severity} sx={{ py: 0 }}>
              {alertaVisible.texto}
            </Alert>
          )}

          {/* isi-print no instalado, sin autorizar, por actualizar o sin licencia en este equipo */}
          {documento && (motivo?.startsWith('isi-print-') || (motivo === 'sin-licencia' && !isi.listo)) && (
            <IsiPrintEstadoPanel isi={isi} />
          )}

          {/* Vincular la impresora registrada con una detectada */}
          {puedeVincular && (
            <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'warning.main' }}>
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Vincular impresora
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Elija la impresora de este equipo que corresponde a “{asignacion?.impresora?.nombre}”. Se
                  guardará su ID de hardware.
                </Typography>
                <FormSelect<PrinterInfo>
                  inputLabel="Impresora detectada"
                  options={isi.impresoras}
                  value={vinculo}
                  onChange={(o) => setVinculoId(o?.id ?? '')}
                  getOptionLabel={(o) => o.name}
                  getOptionValue={(o) => o.id}
                  formatOptionLabel={(o) =>
                    `${o.name} · ${o.port}${o.status !== 'online' ? ` · ${ISI_PRINTER_STATUS_LABEL[o.status]}` : ''}`
                  }
                  noOptionsMessage={() => 'isi-print no detectó impresoras'}
                  menuPosition="fixed"
                />
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<AddLinkOutlined />}
                  loading={accion === 'vincular'}
                  disabled={!vinculo || ocupado}
                  onClick={vincularEImprimir}
                >
                  Vincular e imprimir
                </Button>
              </Stack>
            </Paper>
          )}

          {/* Impresora asignada */}
          {documento && licencia && asignacion && (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Impresora asignada ({asignacion.origen === 'PUNTO_VENTA' ? 'punto de venta' : 'sucursal'})
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                {asignacion.impresora?.nombre ?? asignacion.nombreImpresora}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" noWrap>
                {asignacion.nombreImpresora} · {IMPRESION_FORMATO_LABEL[formatoPdf] ?? formatoPdf} ·{' '}
                {asignacion.copias} copia{asignacion.copias === 1 ? '' : 's'}
              </Typography>
              {vigilarAsignada && asignada.status !== 'red' && asignada.status !== 'desconocido' && (
                <Chip
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1 }}
                  color={asignada.online ? 'success' : 'warning'}
                  icon={asignada.online ? undefined : <CircularProgress size={12} color="inherit" />}
                  label={
                    asignada.online
                      ? 'Lista'
                      : `${
                          asignada.impresora
                            ? ISI_PRINTER_STATUS_LABEL[asignada.impresora.status]
                            : 'No detectada'
                        } · esperando…`
                  }
                />
              )}
              <Button
                fullWidth
                variant="contained"
                startIcon={<PrintOutlined />}
                sx={{ mt: 1 }}
                loading={accion === 'imprimir'}
                disabled={!pdf || ocupado || !isi.listo}
                onClick={imprimirAsignada}
              >
                {motivo ? 'Reintentar' : 'Imprimir'}
              </Button>
            </Paper>
          )}

          <Button
            fullWidth
            variant={documento && licencia && asignacion ? 'outlined' : 'contained'}
            startIcon={<PrintOutlined />}
            loading={accion === 'navegador'}
            disabled={!pdf || ocupado}
            onClick={imprimirNavegador}
          >
            Imprimir con el navegador
          </Button>
          <Button
            fullWidth
            size="small"
            startIcon={<OpenInNew />}
            disabled={!pdfUrl}
            onClick={() => pdfUrl && window.open(pdfUrl, '_blank', 'noopener,noreferrer')}
          >
            Abrir en otra pestaña
          </Button>

          {/* Acciones del proyecto (ej. enviar por WhatsApp) */}
          {impresion.accionesExtra?.({
            documento: documento ?? null,
            titulo: titulo || documentoLabel || 'Documento',
            filename,
            destinatario,
            whatsapp,
            obtenerUrlPublica,
            ocupado,
          })}

          {/* Guardar impresora para el punto de venta */}
          {puedeConfigurar && (
            <>
              <Divider />
              {!mostrarForm ? (
                <Button size="small" startIcon={<TuneOutlined />} onClick={() => setMostrarForm(true)}>
                  {asignacion ? 'Cambiar impresora de este punto de venta' : 'Asignar impresora'}
                </Button>
              ) : (
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Guardar para este punto de venta
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {documentoLabel} se imprimirá siempre en esta impresora en el punto de venta{' '}
                      {user?.puntoVenta?.codigo}.
                    </Typography>
                  </Box>
                  <FormSelect<ImpresoraProps>
                    inputLabel="Impresora"
                    options={impresoras}
                    isLoading={cargandoImpresoras}
                    value={impresoraSeleccionada}
                    onChange={(o) => setImpresoraId(o?._id ?? '')}
                    getOptionLabel={(o) => o.nombre}
                    getOptionValue={(o) => o._id}
                    formatOptionLabel={(o) => (o.atributo1 ? `${o.nombre} · ${o.atributo1}` : o.nombre)}
                    noOptionsMessage={() => 'No hay impresoras registradas en la sucursal'}
                    menuPosition="fixed"
                  />
                  <Stack direction="row" spacing={1}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <FormSelect<OpcionFormato>
                        inputLabel="Formato"
                        options={opcionesFormato}
                        value={opcionesFormato.find((o) => o.value === formato) ?? null}
                        onChange={(o) => o && setFormato(o.value)}
                        isSearchable={false}
                        menuPosition="fixed"
                      />
                    </Box>
                    <TextField
                      label="Copias"
                      type="number"
                      size="small"
                      value={copias}
                      onChange={(e) => setCopias(Number(e.target.value))}
                      sx={{ width: 90 }}
                      slotProps={{ htmlInput: { min: 1, max: IMPRESION_COPIAS_MAX, step: 1 } }}
                    />
                  </Stack>
                  {impresoraSeleccionada && !seleccionEsRed && (
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={isi.impresoras.map((p) => p.id)}
                      renderOption={(props, id) => {
                        const p = isi.impresoras.find((i) => i.id === id)
                        return (
                          <li {...props} key={id}>
                            {p ? `${p.name} · ${p.port}` : id}
                          </li>
                        )
                      }}
                      inputValue={nombreImpresora}
                      onInputChange={(_, v) => setNombreImpresora(v)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Impresora en este equipo (opcional)"
                          placeholder={impresoraSeleccionada.atributo1 || ''}
                          helperText="Solo si en este equipo es otra impresora (ej. compartida)"
                        />
                      )}
                    />
                  )}
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<SaveOutlined />}
                    loading={accion === 'guardar'}
                    disabled={!impresoraSeleccionada || ocupado}
                    onClick={guardarEImprimir}
                  >
                    Guardar e imprimir
                  </Button>
                </Stack>
              )}
              <Button
                size="small"
                color="inherit"
                startIcon={<SettingsOutlined />}
                disabled={ocupado}
                onClick={() => {
                  onClose()
                  navigate(impresoraRoutesMap.documentos.path)
                }}
              >
                Configurar impresoras por documento
              </Button>
            </>
          )}
        </Box>

        {/* Visor */}
        <Box sx={{ flex: 1, position: 'relative', bgcolor: '#525659', display: 'flex', minHeight: 0 }}>
          {pdfUrl ? (
            <>
              {iframeLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2,
                    bgcolor: 'background.paper',
                  }}
                >
                  <CircularProgress size={40} />
                  <Typography sx={{ mt: 2 }} variant="body2" color="text.secondary">
                    Cargando documento...
                  </Typography>
                </Box>
              )}
              <iframe
                key={pdfUrl}
                src={`${pdfUrl}${pdfUrl.includes('#') ? '&' : '#'}toolbar=1`}
                width="100%"
                height="100%"
                style={{ border: 'none', opacity: iframeLoading ? 0 : 1, transition: 'opacity 0.3s ease' }}
                title={titulo ?? 'Documento PDF'}
                onLoad={() => setIframeLoading(false)}
              />
            </>
          ) : pdf ? (
            // Blob recibido: la URL se crea en el efecto del siguiente render
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                color: 'white',
              }}
            >
              <Typography>No se pudo cargar el archivo.</Typography>
            </Box>
          )}
        </Box>
      </Stack>
    </Dialog>
  )
}

/**
 * Diálogo de impresión. En teléfonos y tabletas, donde no se puede instalar isi-print, muestra en su
 * lugar el menú móvil (ver, compartir, descargar, WhatsApp). El dispositivo no cambia durante la
 * sesión, así que la elección es estable.
 */
const ImpresionDocumentoDialog: FunctionComponent<ImpresionDocumentoDialogProps> = (props) =>
  ISI_PRINT_SOPORTADO ? (
    <ImpresionDocumentoDialogEscritorio {...props} />
  ) : (
    <ImpresionDocumentoMovil {...props} />
  )

export default ImpresionDocumentoDialog
